// CareLogix™ ORACLE — AdminOne Funding Intelligence (Phase 33 §11)
// Pipeline view of Federal/Private/Africa funding opportunities with NiSi scoring.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { adminFundingApi } from "@/api/phase33Client";

function Bar({ value, color }) {
  const c = color || scoreColor(value);
  const w = Math.max(2, Math.min(100, Number(value) || 0));
  return (
    <div style={S.barWrap}>
      <motion.div style={{ ...S.barFill, background: c }} initial={{ width: "0%" }} animate={{ width: `${w}%` }} transition={{ duration: 0.8 }} />
    </div>
  );
}

function OppCard({ o }) {
  return (
    <motion.article style={S.card} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid={`admin-funding-card-${o.opportunity_id}`}>
      <header style={S.cardHead}>
        <div>
          <h3 style={S.cardTitle}>{o.title}</h3>
          <div style={S.cardMeta}>
            <span style={S.cardPill}>{o.source}</span>
            <span style={S.cardPill}>{o.region}</span>
            <span style={S.cardPill}>{o.audience.replace(/_/g, " ")}</span>
            <span style={S.cardPill}>~{o.days_to_deadline}d</span>
          </div>
        </div>
        <div style={S.priorityRing}>
          <span style={{ ...S.priorityVal, color: scoreColor(o.scores.priority_score) }}>{o.scores.priority_score}</span>
          <span style={S.priorityLbl}>Priority</span>
        </div>
      </header>
      <div style={S.scoreGrid}>
        {Object.entries(o.scores).map(([k, v]) => (
          <div key={k} style={S.scoreCell}>
            <div style={S.scoreHead}>
              <span style={S.scoreLbl}>{k.replace(/_/g, " ")}</span>
              <span style={{ ...S.scoreVal, color: scoreColor(v) }}>{v}</span>
            </div>
            <Bar value={v} />
          </div>
        ))}
      </div>
      <p style={S.awardRow}>
        <strong style={{ color: PALETTE.goldBright }}>Award range:</strong> ${o.award_range_usd[0].toLocaleString()} – ${o.award_range_usd[1].toLocaleString()}
      </p>
      <p style={S.summary}>{o.logix2_summary}</p>
      <footer style={S.cardFoot}>
        <span style={S.compliance}>Research only · Not financial advice · Human review required.</span>
      </footer>
    </motion.article>
  );
}

export default function AdminFundingIntelligence({ testid = "admin-funding-intelligence" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminFundingApi.dashboard(40);
      setData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) setError("AdminOne authentication required.");
      else setError("Could not load funding pipeline.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const opps = useMemo(() => data?.opportunities || [], [data]);
  const summary = data?.pipeline_summary;

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.header}>
        <div>
          <h2 style={S.title} data-testid={`${testid}-title`}>Funding Intelligence Pipeline™</h2>
          <p style={S.subtitle}>Federal, private foundation, and Africa funding opportunities with NiSi™ scoring.</p>
        </div>
        {summary && (
          <div style={S.summary2}>
            <span style={S.pill}>Total: <strong style={{ color: PALETTE.goldBright }}>{summary.total}</strong></span>
            <span style={S.pill}>High-priority: <strong style={{ color: PALETTE.greenAccent }}>{summary.high_priority}</strong></span>
            <span style={S.pill}>Expiring ≤30d: <strong style={{ color: PALETTE.amber }}>{summary.expiring_30_days}</strong></span>
          </div>
        )}
      </header>
      {error && <div style={S.err} data-testid={`${testid}-error`}>{error}</div>}
      {loading && <div style={S.loading}>Loading pipeline…</div>}
      {!loading && opps.length > 0 && (
        <div style={S.grid} data-testid={`${testid}-grid`}>
          {opps.map((o) => <OppCard o={o} key={o.opportunity_id} />)}
        </div>
      )}
      <footer style={S.foot}>All scores are decision-support intelligence — not award guarantees.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", gap: 14, color: PALETTE.slate3 },
  header:   { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 16 },
  subtitle: { margin: "4px 0 0 0", color: PALETTE.slate4, fontSize: 12 },
  summary2: { display: "flex", gap: 6, flexWrap: "wrap" },
  pill:     { color: PALETTE.slate3, padding: "4px 10px", borderRadius: 999, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11.5, fontFamily: "'Space Mono', monospace" },
  err:      { color: PALETTE.red, fontSize: 12.5, background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}`, padding: "6px 10px", borderRadius: 8 },
  loading:  { color: PALETTE.slate4, fontSize: 12.5 },
  grid:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 10 },
  card:     { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 10 },
  cardHead: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" },
  cardTitle:{ margin: 0, color: PALETTE.goldBright, fontSize: 13, fontWeight: 700 },
  cardMeta: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 },
  cardPill: { color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 10, padding: "2px 7px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, background: "rgba(255,255,255,0.02)" },
  priorityRing:{ display: "flex", flexDirection: "column", alignItems: "center", padding: "6px 8px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 10, background: "rgba(197,160,89,0.05)", minWidth: 60 },
  priorityVal:{ fontFamily: "'Space Mono', monospace", fontSize: 18, fontWeight: 800 },
  priorityLbl:{ color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.10em" },
  scoreGrid:{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 6 },
  scoreCell:{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: 6 },
  scoreHead:{ display: "flex", justifyContent: "space-between", alignItems: "center" },
  scoreLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.06em", textTransform: "capitalize" },
  scoreVal: { fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800 },
  barWrap:  { marginTop: 4, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  barFill:  { height: "100%", width: 0 },
  awardRow: { margin: 0, color: PALETTE.slate3, fontSize: 12 },
  summary:  { margin: 0, color: PALETTE.slate3, fontSize: 12, lineHeight: 1.45 },
  cardFoot: { display: "flex", justifyContent: "flex-end" },
  compliance:{ color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5 },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
